/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import VendorVerificationModal from '../vendor/VendorVerificationModal';
import { useUnreadMessagesCount } from '../../services/messageService';
import { useUnreadNotificationsCount, useUnreadSupportTicketCount } from '../../services/notificationService';
import { usePendingConsultationLeadCount } from '../../services/consultationService';
import { usePendingExternalLeadCount } from '../../services/vendorExternalLeadService';
import LogoutModal from './LogoutModal';
import { 
  LayoutDashboard, 
  User, 
  Calendar, 
  Image, 
  BarChart3, 
  Settings,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CreditCard,
  DollarSign,
  Star,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  Crown,
  Eye,
  Download,
  Upload,
  HelpCircle,
  Zap,
  Target,
  BookOpen,
  FileCheck,
  Bell,
  Shield,
  TrendingUp,
  Lock,
  Camera,
  Gift,
  Heart,
  Users,
  UserCheck,
  FileText,
  FileSignature,
  Wallet,
  Info,
  Share,
  LogOut,
  Palette,
  Headphones,
  Building2 as Building,
  Globe,
  Columns3,
  Workflow,
  Plug,
} from 'lucide-react';

/** Match nav href to pathname without false positives (e.g. /vendor/support vs /vendor/support-tickets). */
function navPathMatches(href, pathname) {
  if (!href || !pathname) return false;
  if (href === '/vendor') {
    return pathname === '/vendor' || pathname === '/vendor/';
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const VendorSidebar = () => {
  const location = useLocation();
  const { 
    isVendorSidebarCollapsed, 
    toggleVendorSidebar, 
    isVendorMobileSidebarOpen, 
    isVendorMobileSidebarCollapsed,
    toggleVendorMobileSidebar,
    closeVendorMobileSidebar 
  } = useSidebar();
  const { user, logout } = useAuth();
  const { canAccessPath, isFreeTier } = useVendorPlanAccess();
  const { 
    isVendorVerified, 
    isVerificationPending,
    showVerificationModal, 
    showVerificationRequired, 
    hideVerificationModal,
    isLoading,
    verificationStatus
  } = useVendorVerification();

  // Get unread counts for badges
  const { data: unreadMessagesCount = 0 } = useUnreadMessagesCount();

  const { data: unreadNotificationsData } = useUnreadNotificationsCount();
  const unreadNotificationsCount = unreadNotificationsData?.data ?? unreadNotificationsData ?? 0;

  const { data: unreadSupportTicketsData } = useUnreadSupportTicketCount();
  const unreadSupportTicketsCount = unreadSupportTicketsData ?? 0;

  const { data: pendingInternalLeads = 0 } = usePendingConsultationLeadCount();
  const { data: pendingExternalLeads = 0 } = usePendingExternalLeadCount();

  const navBadgeCount = (itemName) => {
    if (itemName === 'Messages') return unreadMessagesCount;
    if (itemName === 'Notifications') return unreadNotificationsCount;
    if (itemName === 'Priority support' || itemName === 'Priority Support') {
      return unreadSupportTicketsCount;
    }
    if (itemName === 'Leads') return pendingInternalLeads;
    if (itemName === 'External leads') return pendingExternalLeads;
    return 0;
  };

  const [openDropdowns, setOpenDropdowns] = useState({
    Business: true,
    Services: true,
    Internal: true,
    External: true,
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  /**
   * Sidebar: Home → setup → Internal (marketplace) → External (own clients) → Workspace tools.
   * Combined Internal/External pages are split — each channel has its own dashboard + invoices.
   * See docs/VENDOR_INTERNAL_EXTERNAL_CHANNELS.md
   */
  const navigationGroups = [
    {
      type: 'single',
      items: [
        { name: 'Home', href: '/vendor', icon: LayoutDashboard, requiresVerification: false },
      ],
    },
    {
      type: 'dropdown',
      name: 'Business',
      icon: Building,
      items: [
        { name: 'Account', href: '/vendor/account', icon: User, requiresVerification: false },
        { name: 'Profile preview', href: '/vendor/profile', icon: Eye, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Services',
      icon: Package,
      items: [
        { name: 'Services', href: '/vendor/services', icon: Package, requiresVerification: true },
        { name: 'Packages', href: '/vendor/packages', icon: Gift, requiresVerification: true },
      ],
    },
    { type: 'section', name: 'Internal', hint: 'VendaList marketplace' },
    {
      type: 'dropdown',
      name: 'Internal',
      icon: Globe,
      items: [
        { name: 'Dashboard', href: '/vendor/dashboard', icon: BarChart3, requiresVerification: false },
        { name: 'Bookings', href: '/vendor/bookings', icon: Calendar, requiresVerification: true },
        { name: 'Leads', href: '/vendor/consultations', icon: Columns3, requiresVerification: true },
        { name: 'Invoices', href: '/vendor/internal-invoices', icon: FileText, requiresVerification: true },
        { name: 'Accounting', href: '/vendor/accounting', icon: BookOpen, requiresVerification: true },
        { name: 'Earnings', href: '/vendor/earnings', icon: DollarSign, requiresVerification: true },
        { name: 'Payments', href: '/vendor/payments', icon: CreditCard, requiresVerification: true },
        { name: 'Payouts', href: '/vendor/payout-disbursements', icon: Wallet, requiresVerification: true },
        { name: 'Discount codes', href: '/vendor/discount-codes', icon: Gift, requiresVerification: true },
      ],
    },
    { type: 'section', name: 'External', hint: 'Your own clients' },
    {
      type: 'dropdown',
      name: 'External',
      icon: Lock,
      items: [
        { name: 'Overview', href: '/vendor/external', icon: LayoutDashboard, requiresVerification: false },
        { name: 'External leads', href: '/vendor/external-leads', icon: Columns3, requiresVerification: true },
        { name: 'Private bookings', href: '/vendor/private-bookings', icon: Lock, requiresVerification: true },
        { name: 'Invoices', href: '/vendor/invoices', icon: FileText, requiresVerification: true },
        { name: 'Contracts', href: '/vendor/contracts', icon: FileSignature, requiresVerification: true },
      ],
    },
    { type: 'section', name: 'Workspace', hint: 'Shared across both channels' },
    {
      type: 'single',
      items: [
        { name: 'Workspace', href: '/vendor/workspace', icon: Zap, requiresVerification: true },
        { name: 'Automations', href: '/vendor/automations', icon: Workflow, requiresVerification: true },
        { name: 'Integrations', href: '/vendor/integrations', icon: Plug, requiresVerification: true },
        { name: 'Calendar', href: '/vendor/calendar', icon: Clock, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Showcase',
      icon: Image,
      items: [
        { name: 'Portfolio', href: '/vendor/portfolio', icon: Image, requiresVerification: true },
        { name: 'Photo gallery', href: '/vendor/gallery', icon: Camera, requiresVerification: true },
        { name: 'Custom branding', href: '/vendor/branding', icon: Palette, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Insights',
      icon: BarChart3,
      items: [
        { name: 'Analytics', href: '/vendor/analytics', icon: BarChart3, requiresVerification: true },
        { name: 'Advanced analytics', href: '/vendor/advanced-analytics', icon: TrendingUp, requiresVerification: true },
        { name: 'Performance', href: '/vendor/performance', icon: Target, requiresVerification: true },
        { name: 'Reports', href: '/vendor/reports', icon: FileCheck, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'My Plan',
      icon: Crown,
      items: [
        { name: 'Plans & pricing', href: '/vendor/subscription', icon: CreditCard, requiresVerification: false },
        { name: 'Priority support', href: '/vendor/support-tickets', icon: Headphones, requiresVerification: true },
        { name: 'Advanced booking', href: '/vendor/advanced-bookings', icon: Calendar, requiresVerification: true },
        { name: 'Multi-location', href: '/vendor/locations', icon: MapPin, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Inbox',
      icon: Bell,
      items: [
        { name: 'Notifications', href: '/vendor/notifications', icon: Bell, requiresVerification: true },
        { name: 'Reviews', href: '/vendor/reviews', icon: Star, requiresVerification: true },
        { name: 'Support', href: '/vendor/support', icon: HelpCircle, requiresVerification: false },
      ],
    },
    {
      type: 'dropdown',
      name: 'Settings',
      icon: Settings,
      items: [
        { name: 'Subscription', href: '/vendor/my-subscription', icon: Crown, requiresVerification: false },
        { name: 'Payouts', href: '/vendor/tax-profile', icon: FileCheck, requiresVerification: false },
        { name: 'Security', href: '/vendor/security', icon: Shield, requiresVerification: false },
        { name: 'Preferences', href: '/vendor/preferences', icon: Settings, requiresVerification: false },
      ],
    },
  ];
  // Show all navigation groups (filter items according to plan and upgrade status)
  const getNavigationGroups = () => {
    return navigationGroups
      .map((group) => {
        if (group.type === 'section') return group;

        // Hide "Plans & pricing" from sidebar once vendor has upgraded to a paid subscription
        let items = group.items;
        if (group.name === 'My Plan' && !isFreeTier) {
          items = items.filter((item) => item.href !== '/vendor/subscription');
        }

        if (group.type === 'single') {
          return {
            ...group,
            items: items.filter((item) => canAccessPath(item.href)),
          };
        }
        if (group.type === 'dropdown') {
          return {
            ...group,
            items: items.filter((item) => canAccessPath(item.href)),
          };
        }
        return group;
      })
      .filter((group) => {
        if (group.type === 'section') return true;
        return (group.items?.length ?? 0) > 0;
      })
      // Drop section labels that have no following nav group with items
      .filter((group, index, arr) => {
        if (group.type !== 'section') return true;
        const next = arr[index + 1];
        if (!next || next.type === 'section') return false;
        return (next.items?.length ?? 0) > 0;
      });
  };

  // Keep the section that owns the current route open (reload + in-app navigation)
  useEffect(() => {
    const pathname = location.pathname;
    setOpenDropdowns((prev) => {
      const next = { ...prev };
      let changed = false;
      navigationGroups.forEach((group) => {
        if (group.type !== 'dropdown') return;
        const hasActiveChild = group.items.some((item) =>
          navPathMatches(item.href, pathname)
        );
        if (hasActiveChild && !next[group.name]) {
          next[group.name] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle navigation click
  const handleNavigationClick = (item, e) => {
    // console.log('VendorSidebar: Navigation click:', {
    //   itemName: item.name,
    //   requiresVerification: item.requiresVerification,
    //   isVendorVerified,
    //   isVerificationPending,
    //   showVerificationModal
    // });
    
    if (item.requiresVerification && !isVendorVerified) {
      e.preventDefault();
      console.log('VendorSidebar: Showing verification modal');
      showVerificationRequired();
    }
  };

  return (
    <>
      {isVendorMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-[10990] lg:hidden"
          style={{ background: 'var(--overlay)' }}
          onClick={closeVendorMobileSidebar}
        />
      )}

      <div
        className={`product-sidebar relative bg-product-sidebar text-ink-on-dark lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-40 transition-all duration-300 ${
          isVendorSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        } ${
          isVendorMobileSidebarOpen
            ? `fixed inset-y-0 left-0 z-[10995] flex flex-col ${
                isVendorMobileSidebarCollapsed ? 'w-16' : 'w-64'
              }`
            : 'hidden lg:flex'
        }`}
        style={{ backgroundColor: '#000000', color: '#e3e3e3' }}
      >
      <button
        type="button"
        onClick={() => {
          if (window.innerWidth < 1024) {
            toggleVendorMobileSidebar();
          } else {
            toggleVendorSidebar();
          }
        }}
        className="product-sidebar-toggle"
        aria-label={
          isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed
            ? 'Expand sidebar'
            : 'Collapse sidebar'
        }
      >
        {isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed ? (
          <ChevronRight className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5" />
        )}
      </button>

      <div
        className="product-sidebar-rail flex min-h-0 flex-1 flex-col bg-product-sidebar"
        style={{ backgroundColor: '#000000' }}
      >
        <div className="border-b px-4 py-5" style={{ borderColor: 'var(--sidebar-border)' }}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-product-md text-sm font-semibold text-white"
                style={{ background: 'var(--sidebar-elevated)' }}
              >
                {user?.name ? user.name.charAt(0).toUpperCase() : 'V'}
              </div>
            </div>
            {!isVendorSidebarCollapsed && !isVendorMobileSidebarCollapsed && (
              <div className="ml-3 min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">
                  {user?.name || 'Vendor'}
                </p>
                <p className="truncate text-xs" style={{ color: 'var(--ink-on-dark-muted)' }}>
                  {user?.email || ''}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span
                    className="inline-flex items-center rounded-product-sm px-2 py-0.5 text-[10px] font-medium"
                    style={{ background: 'var(--sidebar-elevated)', color: 'var(--ink-on-dark)' }}
                  >
                    Active
                  </span>
                  {isFreeTier && (
                    <>
                      <span
                        className="inline-flex items-center rounded-product-sm px-2 py-0.5 text-[10px] font-medium"
                        style={{ background: 'var(--sidebar-elevated)', color: 'var(--ink-on-dark-muted)' }}
                      >
                        Free plan
                      </span>
                      <Link
                        to="/vendor/subscription"
                        className="text-[10px] font-semibold text-white underline-offset-2 hover:underline"
                        onClick={() => closeVendorMobileSidebar()}
                      >
                        Upgrade
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 space-y-1.5 overflow-y-auto px-3 py-4 scrollbar-thin">
          {getNavigationGroups().map((group, groupIndex) => {
            const isCollapsed = isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed;

            if (group.type === 'section') {
              if (isCollapsed) {
                return (
                  <div
                    key={`section-${group.name}`}
                    className="mx-2 my-2 border-t"
                    style={{ borderColor: 'var(--sidebar-border)' }}
                    title={group.name}
                  />
                );
              }
              return (
                <div key={`section-${group.name}`} className="px-2.5 pb-1 pt-4 first:pt-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45">
                    {group.name}
                  </p>
                  {group.hint ? (
                    <p className="mt-0.5 text-[10px] text-white/30">{group.hint}</p>
                  ) : null}
                </div>
              );
            }

            if (group.type === 'single') {
              return (
                <React.Fragment key={`single-${groupIndex}`}>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={(e) => handleNavigationClick(item, e)}
                      className={({ isActive }) =>
                        `product-nav-link ${isActive ? 'is-active' : ''} ${
                          item.requiresVerification && !isVendorVerified
                            ? isVerificationPending
                              ? 'opacity-75 cursor-default'
                              : 'opacity-60 cursor-not-allowed'
                            : ''
                        }`
                      }
                      title={isCollapsed ? item.name : undefined}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!isCollapsed && item.name}
                    </NavLink>
                  ))}
                </React.Fragment>
              );
            }

            if (group.type === 'dropdown') {
              const hasActiveChild = group.items.some((item) =>
                navPathMatches(item.href, location.pathname)
              );
              const isDropdownOpen = hasActiveChild || !!openDropdowns[group.name];
              // When section label already names Internal/External, show items only (no nested duplicate header)
              const hideGroupHeader = group.name === 'Internal' || group.name === 'External';

              if (hideGroupHeader) {
                if (isCollapsed) {
                  return (
                    <div key={group.name} className="space-y-0.5">
                      {group.items.map((item) => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          onClick={(e) => handleNavigationClick(item, e)}
                          className={({ isActive }) =>
                            `product-nav-link ${isActive ? 'is-active' : ''}`
                          }
                          title={item.name}
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                        </NavLink>
                      ))}
                    </div>
                  );
                }
                return (
                  <div key={group.name} className="space-y-0.5">
                    {group.items.map((item) => {
                      const badgeCount = navBadgeCount(item.name);

                      return (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          onClick={(e) => handleNavigationClick(item, e)}
                          className={({ isActive }) =>
                            `product-nav-link justify-between ${isActive ? 'is-active' : ''} ${
                              item.requiresVerification && !isVendorVerified
                                ? isVerificationPending
                                  ? 'opacity-75 cursor-default'
                                  : 'opacity-60 cursor-not-allowed'
                                : ''
                            }`
                          }
                        >
                          <div className="flex min-w-0 items-center">
                            <item.icon className="h-4 w-4 mr-2.5 shrink-0" />
                            <span className="truncate">{item.name}</span>
                          </div>
                          {badgeCount > 0 && (
                            <span className="ml-2 min-w-[1.15rem] rounded-full bg-surface px-1.5 py-0.5 text-center text-[10px] font-semibold text-ink">
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div key={group.name} className="space-y-0.5">
                  {groupIndex > 0 && !isCollapsed && (
                    <div className="px-2.5 pb-1 pt-2 first:pt-0" aria-hidden />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (hasActiveChild) return;
                      toggleDropdown(group.name);
                    }}
                    className={`product-nav-link w-full ${ isDropdownOpen || hasActiveChild ? 'is-active' : '' }`}
                    title={isCollapsed ? group.name : undefined}
                    aria-expanded={isDropdownOpen}
                  >
                    <group.icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left tracking-tight">{group.name}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${ isDropdownOpen ? 'rotate-180' : '' }`}
                        />
                      </>
                    )}
                  </button>

                  {!isCollapsed && isDropdownOpen && (
                    <div
                      className="ml-3 space-y-0.5 border-l pl-2"
                      style={{ borderColor: 'var(--sidebar-border)' }}
                    >
                      {group.items.map((item) => {
                        const badgeCount = navBadgeCount(item.name);

                        return (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            onClick={(e) => handleNavigationClick(item, e)}
                            className={({ isActive }) =>
                              `product-nav-link justify-between ${isActive ? 'is-active' : ''} ${
                                item.requiresVerification && !isVendorVerified
                                  ? isVerificationPending
                                    ? 'opacity-75 cursor-default'
                                    : 'opacity-60 cursor-not-allowed'
                                  : ''
                              }`
                            }
                          >
                            <div className="flex min-w-0 items-center">
                              <item.icon className="h-4 w-4 mr-2.5 shrink-0" />
                              <span className="truncate">{item.name}</span>
                            </div>
                            {badgeCount > 0 && (
                              <span className="ml-2 min-w-[1.15rem] rounded-full bg-surface px-1.5 py-0.5 text-center text-[10px] font-semibold text-ink">
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </nav>

        <div className="border-t px-4 py-4" style={{ borderColor: 'var(--sidebar-border)' }}>
          <button
            type="button"
            onClick={() => setShowLogoutModal(true)}
            className="product-nav-link w-full"
            title={(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? 'Logout' : undefined}
          >
            <LogOut className="h-5 w-5" />
            {!(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) && 'Logout'}
          </button>
        </div>
      </div>
      </div>
      
      {/* Verification Modal */}
      <VendorVerificationModal
        isOpen={showVerificationModal}
        onClose={hideVerificationModal}
        isPending={isVerificationPending}
      />

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => !loggingOut && setShowLogoutModal(false)}
        onConfirm={handleLogout}
        loading={loggingOut}
      />
    </>
  );
};

export default VendorSidebar;
